/**
 * /cost-code-templates — global template catalog browser.
 *
 * Lists every registered template with its trade, project_type,
 * version, status, and levels_enabled. Click a template → drawer
 * shows its node hierarchy (DataGridPro tree-data) + an "Apply
 * to current entity" action.
 *
 * Templates are GLOBAL (not entity-scoped). Editing template
 * structure requires admin role; applying requires accountant.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useConstructionPack } from "@/lib/useConstructionPack";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  MenuItem,
  Switch,
  TextField,
} from "@mui/material";
import { DataGridPro, type GridColDef } from "@mui/x-data-grid-pro";
import { gridDefaults, FlexGrid } from "@/grids/gridDefaults";

import { api } from "@/lib/api";
import {
  costCodeKeys,
  costCodeTemplateKeys,
  costCodeTemplateNodesQueryOptions,
  costCodeTemplatesQueryOptions,
} from "@/lib/queries";
import { useToast } from "@/components/Toaster";
import type {
  CostCodeTemplate,
  CostCodeTemplateApplyResult,
} from "@/lib/types";

export const Route = createFileRoute("/_auth/cost-code-templates")({
  component: CostCodeTemplatesPage,
});


const STATUS_BG: Record<CostCodeTemplate["status"], string> = {
  draft:      "#f1f5f9",
  active:     "#dcfce7",
  deprecated: "#fef3c7",
  archived:   "#e5e7eb",
};
const STATUS_FG: Record<CostCodeTemplate["status"], string> = {
  draft:      "#475569",
  active:     "#166534",
  deprecated: "#92400e",
  archived:   "#374151",
};


function CostCodeTemplatesPage() {
  const { enabled: constructionEnabled } = useConstructionPack();
  if (!constructionEnabled) return null;
  const tmplsQuery = useQuery(costCodeTemplatesQueryOptions());
  const templates = tmplsQuery.data ?? [];

  const [selected, setSelected] = useState<CostCodeTemplate | null>(null);
  const [applying, setApplying] = useState<CostCodeTemplate | null>(null);

  const columns = useMemo<GridColDef<CostCodeTemplate>[]>(
    () => [
      {
        field: "name", headerName: "Template", flex: 1, minWidth: 280,
        renderCell: (p) => (
          <div>
            <div className="text-sm font-medium">{p.row.name}</div>
            <div className="text-[10px] text-slate-500 dark:text-slate-400">
              v{p.row.version}
              {p.row.trade && ` · ${p.row.trade}`}
              {p.row.project_type && ` · ${p.row.project_type}`}
            </div>
          </div>
        ),
      },
      {
        field: "status", headerName: "Status", width: 120,
        renderCell: (p) => (
          <Chip size="small" label={p.row.status}
            sx={{
              fontSize: 10, height: 20,
              bgcolor: STATUS_BG[p.row.status],
              color: STATUS_FG[p.row.status],
              fontWeight: 600,
            }} />
        ),
      },
      {
        field: "levels_enabled", headerName: "Levels", width: 80,
        type: "number",
      },
      {
        field: "default_modifiers", headerName: "Default modifiers",
        flex: 1, minWidth: 220,
        renderCell: (p) => (
          <span className="text-xs text-slate-600 dark:text-slate-300">
            {(p.row.default_modifiers ?? []).join(", ") || "—"}
          </span>
        ),
      },
      {
        field: "actions", headerName: "", width: 130, sortable: false,
        filterable: false,
        renderCell: (p) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Button size="small"
              disabled={p.row.status !== "active"}
              onClick={() => setApplying(p.row)}>
              Apply
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-baseline gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Cost Code Templates
          </h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Global recipes — apply into an entity to seed its cost-code list
          </span>
        </div>
      </div>

      <div className="flex-1 min-h-0 px-6 py-3">
        <FlexGrid>
          <DataGridPro
            {...gridDefaults}
            rows={templates}
            columns={columns}
            getRowId={(r) => r.id}
            loading={tmplsQuery.isLoading}
            density="standard"
            onRowClick={(p) => setSelected(p.row as CostCodeTemplate)}
            sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
          />
        </FlexGrid>
      </div>

      <Drawer anchor="right" open={selected !== null}
        onClose={() => setSelected(null)}
        PaperProps={{ sx: { width: 560 } }}>
        {selected && (
          <TemplateDrawer template={selected}
            onClose={() => setSelected(null)}
            onApply={() => { setApplying(selected); setSelected(null); }} />
        )}
      </Drawer>

      {applying && (
        <ApplyTemplateDialog template={applying}
          onClose={() => setApplying(null)} />
      )}
    </div>
  );
}


function TemplateDrawer({
  template, onClose, onApply,
}: {
  template: CostCodeTemplate;
  onClose: () => void;
  onApply: () => void;
}) {
  const nodesQuery = useQuery(costCodeTemplateNodesQueryOptions(template.id));
  const nodes = nodesQuery.data ?? [];

  const rowsWithPath = useMemo(() => {
    const byId = new Map(nodes.map((n) => [n.id, n]));
    return nodes.map((n) => {
      const path: string[] = [];
      let cur: typeof n | undefined = n;
      let i = 0;
      while (cur && i < 8) {
        path.unshift(cur.code);
        cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
        i++;
      }
      return { ...n, path };
    });
  }, [nodes]);

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-slate-200 dark:border-gray-800">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">{template.name}</span>
          <span className="text-xs text-slate-500 dark:text-slate-400">v{template.version}</span>
          <div className="flex-1" />
          <IconButton size="small" onClick={onClose}>✕</IconButton>
        </div>
        <div className="flex items-center gap-2 mt-1 text-xs text-slate-600 dark:text-slate-300">
          {template.trade && <span>Trade: {template.trade}</span>}
          {template.project_type && <span>· {template.project_type}</span>}
          <span>· {template.levels_enabled} levels</span>
        </div>
        {template.default_modifiers.length > 0 && (
          <div className="mt-2 flex gap-1 flex-wrap">
            {template.default_modifiers.map((m) => (
              <Chip key={m} size="small" label={m}
                sx={{ fontSize: 10, height: 18 }} />
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-hidden p-3">
        <FlexGrid>
          <DataGridPro
            {...gridDefaults}
            rows={rowsWithPath}
            columns={[
              { field: "code", headerName: "Code", width: 130,
                renderCell: (p) => (
                  <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                    {p.row.code}
                  </span>
                ),
              },
              { field: "label", headerName: "Label", flex: 1, minWidth: 200 },
              { field: "default_cost_type", headerName: "Type", width: 100 },
              { field: "unit_of_measure", headerName: "UoM", width: 70 },
            ]}
            treeData
            getTreeDataPath={(r) => r.path}
            groupingColDef={{ headerName: "Hierarchy", width: 220 }}
            defaultGroupingExpansionDepth={2}
            getRowId={(r) => r.id}
            loading={nodesQuery.isLoading}
            density="compact"
          />
        </FlexGrid>
      </div>

      <div className="border-t border-slate-200 p-3 dark:border-gray-800">
        <Button fullWidth variant="contained"
          disabled={template.status !== "active"} onClick={onApply}>
          Apply to current entity
        </Button>
      </div>
    </div>
  );
}


function ApplyTemplateDialog({
  template, onClose,
}: {
  template: CostCodeTemplate;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const [skipExisting, setSkipExisting] = useState(true);
  const [codePrefix, setCodePrefix] = useState("");
  const [result, setResult] = useState<CostCodeTemplateApplyResult | null>(null);

  const applyMut = useMutation({
    mutationFn: () =>
      api<CostCodeTemplateApplyResult>(
        `/api/cost-code-templates/${template.id}/apply`,
        {
          method: "POST",
          body: { skip_existing: skipExisting, code_prefix: codePrefix },
        },
      ),
    onSuccess: (r) => {
      setResult(r);
      qc.invalidateQueries({ queryKey: costCodeKeys.all });
      qc.invalidateQueries({ queryKey: costCodeTemplateKeys.all });
      toast.success(`Applied: ${r.created} created, ${r.skipped} skipped`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Apply: {template.name}</DialogTitle>
      <DialogContent dividers>
        <div className="space-y-3 pt-2">
          <div className="text-xs text-slate-600 dark:text-slate-300">
            Copies every node from this template into the current entity's
            cost-code list. Idempotent — pre-existing codes are skipped
            when "Skip existing" is on.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Code prefix (optional)" size="small"
              placeholder="JOB42-" value={codePrefix}
              onChange={(e) => setCodePrefix(e.target.value)}
              helperText="Prepended to every code" />
            <label className="flex items-center gap-2 text-xs text-slate-600 self-center dark:text-slate-300">
              <Switch size="small" checked={skipExisting}
                onChange={(_, v) => setSkipExisting(v)} />
              Skip existing
            </label>
          </div>
          {result && (
            <div className="border border-slate-200 rounded p-3 bg-slate-50 dark:bg-gray-950 dark:border-gray-800">
              <div className="text-sm font-medium mb-1">Done</div>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div>
                  <div className="text-slate-500 dark:text-slate-400">Created</div>
                  <div className="text-emerald-700 font-mono text-lg">
                    {result.created}
                  </div>
                </div>
                <div>
                  <div className="text-slate-500 dark:text-slate-400">Skipped</div>
                  <div className="text-slate-600 font-mono text-lg dark:text-slate-300">
                    {result.skipped}
                  </div>
                </div>
                <div>
                  <div className="text-slate-500 dark:text-slate-400">Errors</div>
                  <div className="text-red-700 font-mono text-lg">
                    {result.errors.length}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained"
          disabled={applyMut.isPending} onClick={() => applyMut.mutate()}>
          {applyMut.isPending ? "Applying…" : "Apply"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
